package com.anjuros.website.redesign.sections.pricing

import androidx.compose.runtime.Composable
import com.anjuros.website.components.language.LocalText
import com.anjuros.website.redesign.components.RdScrollTable
import com.anjuros.website.redesign.components.RdSection
import com.anjuros.website.redesign.components.RdSectionHead
import com.anjuros.website.redesign.styles.PricingDashStyle
import com.anjuros.website.redesign.styles.PricingTableGroupStyle
import com.anjuros.website.redesign.styles.PricingTableNoteStyle
import com.anjuros.website.redesign.styles.PricingTableStyle
import com.anjuros.website.redesign.styles.PricingTableWrapStyle
import com.anjuros.website.redesign.styles.PricingTickStyle
import com.anjuros.website.redesign.styles.RdSectionStyle
import com.anjuros.website.redesign.util.RdText
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.attributes.Scope
import org.jetbrains.compose.web.attributes.colspan
import org.jetbrains.compose.web.attributes.scope
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Small
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr

/** Every care plan side by side, grouped by what it does for you. Full Service is highlighted. */
@Composable
fun PricingCompareSection() {
    RdSection(
        id = "compare",
        labelledBy = "compare-h",
        modifier = RdSectionStyle.toModifier(),
    ) {
        RdSectionHead(RdText.PRICING_COMPARE_EYEBROW, "compare-h", RdText.PRICING_COMPARE_TITLE)
        RdScrollTable(PricingTableWrapStyle.toModifier(), radiusPx = 16) {
            Table(PricingTableStyle.toModifier().toAttrs()) {
                Thead {
                    Tr {
                        Th({ scope(Scope.Col) }) { Text(LocalText(RdText.PRICING_COMPARE_COLUMN)) }
                        CarePlan.entries.forEach { plan ->
                            Th({ scope(Scope.Col) }) {
                                Text(LocalText(plan.title))
                                Small { Text(LocalText(plan.price) + LocalText(RdText.PRICE_PER_MONTH)) }
                            }
                        }
                    }
                }
                Tbody {
                    CompareGroups.forEach { group ->
                        Tr(PricingTableGroupStyle.toModifier().toAttrs()) {
                            // The heading spans every column; its text sits in a span so it can stay in view while swiping.
                            Th({ colspan(4); scope(Scope.Colgroup) }) { Span { Text(LocalText(group.title)) } }
                        }
                        group.rows.forEach { row ->
                            Tr {
                                Th({ scope(Scope.Row) }) { Text(LocalText(row.feature)) }
                                listOf(row.basic, row.plus, row.full).forEach { cell -> Td { CellContent(cell) } }
                            }
                        }
                    }
                }
            }
        }
        P(PricingTableNoteStyle.toModifier().toAttrs()) { Text(LocalText(RdText.PRICING_COMPARE_NOTE)) }
    }
}

@Composable
private fun CellContent(cell: CompareCell) {
    when (cell) {
        CompareCell.Yes -> Span(PricingTickStyle.toModifier().toAttrs()) { Text("✓") }
        CompareCell.No -> {
            val label = LocalText(RdText.PRICING_NOT_INCLUDED)
            Span(PricingDashStyle.toModifier().attrsModifier { attr("aria-label", label) }.toAttrs()) { Text("✕") }
        }
        is CompareCell.Value -> Text(LocalText(cell.text))
        CompareCell.BasicUpdates -> BasicUpdatesCell()
    }
}
